import jsPDF from 'jspdf';
import { autoTable } from 'jspdf-autotable';
import { loadLogoPngDataUrl } from './brandLogo';

/**
 * Branded PDF invoice for the company billing report.
 *
 * Sits beside the Excel export rather than replacing it: the spreadsheet is for
 * working with the numbers, this is the thing a company actually files or sends
 * to their finance team, so it is laid out as a document rather than a grid.
 *
 * BRAND TOKENS are taken from what the app already uses, not invented — #E53E14
 * is the primary (134 occurrences across src/), #2D3D5C the dark navy, #FFF5F3
 * the light brand tint behind table headings, #363538 body ink.
 *
 * TYPEFACE: jsPDF's built-in Helvetica. The brand face is Rubik, but
 * `@fontsource/rubik` ships only woff/woff2 and jsPDF can embed only TTF, so
 * matching it exactly would mean sourcing and base64-ing the TTFs. Colour, logo
 * and layout carry the brand here instead.
 */

const BRAND = '#E53E14';
const NAVY = '#2D3D5C';
const TINT = '#FFF5F3';
const INK = '#363538';
const MUTED = '#A8AAAD';
const RULE = '#E5E7EB';

const PAGE_W = 210;
const PAGE_H = 297;
const MARGIN = 16;

/** `#RRGGBB` -> `[r, g, b]`, which is what jsPDF wants. */
const rgb = (hex) => {
    const h = hex.replace('#', '');
    return [parseInt(h.slice(0, 2), 16), parseInt(h.slice(2, 4), 16), parseInt(h.slice(4, 6), 16)];
};

const money = (n) => {
    const v = Number(n);
    if (!Number.isFinite(v)) return '-';
    return `$ ${v.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};


/**
 * ⚠️ ONE LINE PER TIER, not one line per head count.
 *
 * Pricing is progressive: a company's members are split across bands and each
 * band is charged at its own rate. An invoice that showed a single "price per
 * user" would not add up — the customer would multiply it by the head count and
 * get a different number from the total, which is the fastest way to lose an
 * argument about a bill. The per-band working is the invoice.
 *
 * Nothing is computed here. `lines`, `subtotal` and `total` come from the API,
 * which is the same code that produces the stored invoice; this file only lays
 * them out.
 *
 * @param companyName      billed-to name
 * @param billingMonth     already formatted, e.g. "October 2026"
 * @param billedOn         which population is charged, e.g. "Members who accepted their invitation"
 * @param billableUsers    the head count the bands were applied to
 * @param lines            [{ tier, from, to, rate, users, charge }] — bands with no users are dropped
 * @param subtotal         number, before any discount
 * @param discountPercent  number or null — the admin report offers it, the company one does not
 * @param total            number, after the discount
 * @param blendedRate      number — total divided by head count, shown for reference only
 * @param notes            array of strings printed under the table
 * @param fileName         e.g. "Acme-Oct-2026-Invoice.pdf"
 */
export const downloadInvoicePdf = async ({
    companyName,
    billingMonth,
    billedOn,
    billableUsers,
    lines = [],
    subtotal,
    discountPercent = null,
    total,
    blendedRate,
    notes = [],
    fileName = 'Invoice.pdf',
}) => {
    const doc = new jsPDF({ unit: 'mm', format: 'a4', compress: true });
    // Shared with the Excel exports so both documents carry the same mark at
    // the same fidelity. Null when it cannot be loaded -- see brandLogo.js.
    const logoAsset = await loadLogoPngDataUrl(4);
    const logo = logoAsset && logoAsset.dataUrl;

    // ---------------------------------------------------------------- header
    // Full-bleed navy band with a brand accent stripe under it. The stripe is
    // what makes it read as CFT rather than as a generic dark header.
    doc.setFillColor(...rgb(NAVY));
    doc.rect(0, 0, PAGE_W, 34, 'F');
    doc.setFillColor(...rgb(BRAND));
    doc.rect(0, 34, PAGE_W, 2.5, 'F');

    if (logo) {
        doc.addImage(logo, 'PNG', MARGIN, 8, 16.5, 17.7);
    }
    const textLeft = logo ? MARGIN + 22 : MARGIN;

    doc.setTextColor(255, 255, 255);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(13);
    doc.text('CONTINGENT FITNESS', textLeft, 16);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(7.5);
    doc.setTextColor(190, 198, 212);
    doc.text('TRAINING, INC.', textLeft, 21);

    // "INVOICE" right-aligned, letterspaced by hand — jsPDF has no tracking.
    doc.setTextColor(255, 255, 255);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(20);
    doc.text('I N V O I C E', PAGE_W - MARGIN, 17, { align: 'right' });
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);
    doc.setTextColor(...rgb(BRAND));
    doc.text(String(billingMonth || ''), PAGE_W - MARGIN, 24.5, { align: 'right' });

    // ------------------------------------------------------- billed to / meta
    let y = 50;
    const colR = PAGE_W / 2 + 6;

    const label = (text, x, yy) => {
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(7);
        doc.setTextColor(...rgb(MUTED));
        doc.text(text.toUpperCase(), x, yy);
    };
    const value = (text, x, yy, size = 10.5) => {
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(size);
        doc.setTextColor(...rgb(INK));
        doc.text(String(text ?? '-'), x, yy);
    };

    label('Billed to', MARGIN, y);
    value(companyName || '-', MARGIN, y + 6, 12);

    label('Billing period', colR, y);
    value(billingMonth || '-', colR, y + 6);

    label('Issued', colR, y + 15);
    value(
        new Date().toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }),
        colR,
        y + 21
    );

    label('Billed on', MARGIN, y + 15);
    value(
        billedOn
            ? `${billedOn}${Number.isFinite(Number(billableUsers)) ? ` (${Number(billableUsers).toLocaleString()})` : ''}`
            : '-',
        MARGIN,
        y + 21,
        9
    );

    y += 34;
    doc.setDrawColor(...rgb(RULE));
    doc.setLineWidth(0.3);
    doc.line(MARGIN, y, PAGE_W - MARGIN, y);

    // ------------------------------------------------------------- the table
    // Bands with nobody in them are dropped: printing "Tier 4 — 0 users — $0.00"
    // on a 23-person company's invoice invites the question of why a tier for
    // 100,000 users is on their bill at all.
    const used = (lines || []).filter((l) => Number(l.users) > 0);

    const bandLabel = (l) =>
        l.to === null || l.to === undefined
            ? `${Number(l.from).toLocaleString()}+`
            : `${Number(l.from).toLocaleString()}–${Number(l.to).toLocaleString()}`;

    const body = used.length
        ? used.map((l) => [
              `Tier ${l.tier}`,
              `Users ${bandLabel(l)} at ${money(l.rate)} each`,
              Number(l.users).toLocaleString(),
              money(l.rate),
              money(l.charge),
          ])
        : [['No billable users', 'Nobody on the roster for this period', '0', '-', money(0)]];

    // Shown as its own line once there is more than one band, because the bands
    // above it no longer share a single unit price and a reader has no other way
    // to check the arithmetic.
    if (used.length > 1) {
        body.push(['Subtotal', 'Sum of the tiers above', '-', '-', money(subtotal)]);
    }

    // Last, not first: it is an adjustment to the lines above it, so it reads as
    // a footnote to them rather than as an item in its own right.
    if (discountPercent) {
        body.push([
            'Discount applied',
            `${discountPercent}% off the subtotal`,
            '-',
            `${discountPercent} %`,
            `-${money((Number(subtotal) || 0) - (Number(total) || 0))}`,
        ]);
    }

    autoTable(doc, {
        startY: y + 8,
        head: [['Item', 'Description', 'Qty', 'Unit', 'Amount']],
        body,
        theme: 'plain',
        styles: {
            font: 'helvetica',
            fontSize: 9,
            cellPadding: { top: 3.4, bottom: 3.4, left: 3, right: 3 },
            textColor: rgb(INK),
            lineColor: rgb(RULE),
            lineWidth: { bottom: 0.2 },
        },
        headStyles: {
            fillColor: rgb(TINT),
            textColor: rgb(NAVY),
            fontStyle: 'bold',
            fontSize: 7.5,
            lineWidth: 0,
        },
        columnStyles: {
            0: { cellWidth: 42, fontStyle: 'bold' },
            1: { textColor: rgb(MUTED), fontSize: 8.5 },
            2: { cellWidth: 16, halign: 'right' },
            3: { cellWidth: 22, halign: 'right' },
            4: { cellWidth: 28, halign: 'right', fontStyle: 'bold' },
        },
        margin: { left: MARGIN, right: MARGIN },
    });

    // --------------------------------------------------------------- total
    let afterTable = (doc.lastAutoTable && doc.lastAutoTable.finalY) || y + 40;
    const boxW = 78;
    const boxX = PAGE_W - MARGIN - boxW;
    afterTable += 6;

    const boxH = Number.isFinite(Number(blendedRate)) && Number(blendedRate) > 0 ? 23 : 18;

    doc.setFillColor(...rgb(TINT));
    doc.rect(boxX, afterTable, boxW, boxH, 'F');
    // A brand rule down the left edge of the total, so the eye lands on it.
    doc.setFillColor(...rgb(BRAND));
    doc.rect(boxX, afterTable, 1.6, boxH, 'F');

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(7.5);
    doc.setTextColor(...rgb(MUTED));
    doc.text('AMOUNT DUE', boxX + 7, afterTable + 7);

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(16);
    doc.setTextColor(...rgb(BRAND));
    doc.text(money(total), PAGE_W - MARGIN - 6, afterTable + 13.5, { align: 'right' });

    // The average the customer will work out for themselves anyway. Labelled as
    // an average so nobody mistakes it for a rate they could multiply by a new
    // head count and get next month's bill.
    if (boxH > 18) {
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(7);
        doc.setTextColor(...rgb(MUTED));
        doc.text(
            `Average ${money(blendedRate)} per user across all tiers`,
            PAGE_W - MARGIN - 6,
            afterTable + 19.5,
            { align: 'right' }
        );
    }

    // --------------------------------------------------------------- notes
    let ny = afterTable + boxH + 12;
    if (notes.length) {
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(7);
        doc.setTextColor(...rgb(MUTED));
        doc.text('NOTES', MARGIN, ny);
        ny += 5;
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(8);
        doc.setTextColor(...rgb(MUTED));
        notes.forEach((n) => {
            const lines = doc.splitTextToSize(String(n), PAGE_W - MARGIN * 2);
            doc.text(lines, MARGIN, ny);
            ny += lines.length * 4 + 1.5;
        });
    }

    // -------------------------------------------------------------- footer
    doc.setDrawColor(...rgb(RULE));
    doc.setLineWidth(0.3);
    doc.line(MARGIN, PAGE_H - 18, PAGE_W - MARGIN, PAGE_H - 18);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(7.5);
    doc.setTextColor(...rgb(MUTED));
    doc.text(
        `© ${new Date().getFullYear()}, CONTINGENT FITNESS TRAINING, INC. All Rights Reserved.`,
        MARGIN,
        PAGE_H - 12
    );
    doc.text('Page 1 of 1', PAGE_W - MARGIN, PAGE_H - 12, { align: 'right' });

    doc.save(fileName);
};

export default downloadInvoicePdf;
